/* eslint-disable */
// @ts-nocheck
/* ============================================================================
 * MTE — Dictee UI (Track J.3b — 2026-09-14)
 * ----------------------------------------------------------------------------
 * Source-of-truth : shared/mte-product/03-skill-taxonomy.md
 * Owner : Atelier (Track J.3b)
 *
 * Archetype : "dictee" — Sage dita un audio, l'enfant écrit mot-à-mot.
 *             DISTINCTION IMPORTANTE :
 *               - Dictee       = Sage PARLE, enfant ÉCRIT (cette UI)
 *               - LectureVoixHaute = enfant PARLE, Sage/Système écoute
 *
 * Props (canonical, see lib/banque-types.ts):
 *   - item          : BanqueItemDictee
 *   - onAnswer      : (correct: boolean, payload?: unknown) => void
 *   - disabled?     : boolean
 *   - showFeedback? : 'idle' | 'correct' | 'incorrect'
 *
 * Payload returned via onAnswer : { perToken: Record<string, boolean>, totalCorrect: boolean }
 * ========================================================================== */

import React, { useState, useMemo, useRef } from 'react';
import type { BanqueItemDictee } from '../../../../lib/banque-types';
import { isAnswerCorrect, normalizeAnswer } from '../../../../lib/banque-types';

export interface DicteeProps {
  item: BanqueItemDictee;
  onAnswer: (correct: boolean, payload?: unknown) => void;
  disabled?: boolean;
  showFeedback?: 'idle' | 'correct' | 'incorrect';
}

export function Dictee({
  item,
  onAnswer,
  disabled = false,
  showFeedback = 'idle',
}: DicteeProps): JSX.Element {
  const tokens = useMemo<string[]>(
    () => normalizeAnswer(item.bonneReponse).split(/\s+/u).filter(Boolean),
    [item.bonneReponse],
  );

  const [values, setValues] = useState<string[]>(tokens.map(() => ''));
  const [playCount, setPlayCount] = useState<number>(0);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlay = (): void => {
    if (disabled || submitted) return;
    if (playCount >= item.replayMax) return;
    setPlayCount((c) => c + 1);
    const audio = audioRef.current;
    if (audio) {
      void audio.play();
    }
  };

  const allFilled = values.every((v) => v.trim().length > 0);

  const handleTokenChange = (idx: number, value: string): void => {
    setValues((prev) => prev.map((v, i) => (i === idx ? value : v)));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (disabled || submitted || !allFilled) return;
    setSubmitted(true);
    const perToken: Record<string, boolean> = {};
    let totalCorrect = true;
    for (let i = 0; i < tokens.length; i++) {
      const ok = isAnswerCorrect(values[i], tokens[i], 0);
      perToken[`T${i + 1}`] = ok;
      if (!ok) totalCorrect = false;
    }
    onAnswer(totalCorrect, { perToken, tokens, totalCorrect });
  };

  const feedbackClass = submitted
    ? showFeedback !== 'idle'
      ? showFeedback
      : values.every((v, i) => isAnswerCorrect(v, tokens[i], 0))
      ? 'correct'
      : 'incorrect'
    : 'idle';

  const replayDisabled = playCount >= item.replayMax || disabled || submitted;

  return (
    <form
      className="dictee"
      data-testid="dictee"
      data-archetype="dictee"
      data-item-id={item.id}
      data-feedback={feedbackClass}
      onSubmit={handleSubmit}
    >
      <p className="dictee-enonce" aria-label="Énoncé">
        {item.enonce}
      </p>
      {item.consigne && (
        <p className="dictee-consigne">{item.consigne}</p>
      )}
      <div className="dictee-audio-block" data-testid="dictee-audio-block">
        <button
          type="button"
          className="dictee-replay"
          data-action="replay"
          data-testid="dictee-replay"
          onClick={handlePlay}
          disabled={replayDisabled}
          aria-label={`Réécouter le Sage (${item.replayMax - playCount} écoutes restantes)`}
        >
          ▶ Réécouter ({item.replayMax - playCount})
        </button>
        <span
          className="dictee-play-counter"
          data-testid="dictee-play-counter"
          aria-live="polite"
        >
          Écoutes&nbsp;: {playCount}/{item.replayMax}
        </span>
        <audio
          ref={audioRef}
          src={item.audioUrl}
          preload="none"
          data-testid="dictee-audio"
        />
      </div>
      <ol className="dictee-tokens" data-testid="dictee-tokens">
        {tokens.map((tok, idx) => (
          <li key={`tok-${idx}`} className="dictee-token">
            <label htmlFor={`dictee-tok-${item.id}-${idx}`}>
              Mot {idx + 1}&nbsp;:
            </label>
            <input
              id={`dictee-tok-${item.id}-${idx}`}
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="dictee-token-input"
              data-testid={`dictee-tok-${idx + 1}`}
              data-feedback={
                submitted
                  ? isAnswerCorrect(values[idx], tok, 0)
                    ? 'correct'
                    : 'incorrect'
                  : 'idle'
              }
              value={values[idx]}
              onChange={(e) => handleTokenChange(idx, e.target.value)}
              disabled={disabled || submitted}
            />
          </li>
        ))}
      </ol>
      <button
        type="submit"
        className="dictee-submit"
        data-action="soumettre"
        data-testid="dictee-submit"
        disabled={!allFilled || submitted || disabled}
      >
        Valider
      </button>
      {submitted && (
        <p className="dictee-feedback" data-testid="dictee-feedback">
          {values.every((v, i) => isAnswerCorrect(v, tokens[i], 0))
            ? 'Bravo, tu as bien écrit tous les mots !'
            : 'Quelques mots sont à corriger.'}
        </p>
      )}
    </form>
  );
}

export default Dictee;
